import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  AfterViewInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';

import { FormInputComponent } from '../form-input/form-input.component';
import { FormOption } from '../form-option';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TypedFormGroup } from '@feature/forms/form-model';
import { ReadOnlyUserService } from '@core/read-only-user/read-only-user.service';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wai-form-dropdown',
  templateUrl: './form-dropdown.component.html',
  styleUrls: ['./../form-controls.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormDropdownComponent extends FormInputComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() public tooltip?: string;
  @ViewChild('select') select: MatSelect;
  @Input() public isCheckboxDropdown = false;
  @Input() public isMultiselect = false;
  @Input() public isDisabled: boolean;
  @Input() public options: FormOption[];
  @Input() public prefixIcon: string = null;
  @Input() public showEmptyValue = true;
  @Input() public cannotFindValue = false;
  @Input() public separateFirstOption = false;
  @Input() public selectedIndex: number | null = null;
  @Output() public selectionChange = new EventEmitter<MatSelectChange>();
  allSelected = false;
  public defaultSelection = null;

  private readOnlyUserService = inject(ReadOnlyUserService);
  private controlStatusSubscription?: Subscription;

  public ngOnChanges(changes: SimpleChanges): void {
    // Update disabled state when isDisabled input or control changes
    if ((changes['isDisabled'] || changes['control']) && this.control) {
      this.updateDisabledState();
    }
  }

  public ngOnInit(): void {
    // Subscribe to control status changes to react when form is disabled/enabled
    // This is in addition to BaseInputComponent's subscription which handles change detection
    if (this.control) {
      this.controlStatusSubscription = this.control.statusChanges.subscribe(() => {
        // Only update if isDisabled is not explicitly set
        if (this.isDisabled === undefined || this.isDisabled === null) {
          this.updateDisabledState();
        }
      });
    }
  }

  public ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.controlStatusSubscription) {
      this.controlStatusSubscription.unsubscribe();
    }
  }

  public ngAfterViewInit(): void {
    if (this.selectedIndex && this.options && this.options.length > 0) {
      this.defaultSelection = this.options[this.selectedIndex].value;
    }

    this.updateDisabledState();
  }

  /**
   * Updates the disabled state of the dropdown.
   *
   * Priority order:
   * 1. Explicit `isDisabled` input (if set, use that value)
   * 2. Read-only user status (if user is read-only, disable)
   * 3. Control's disabled state (if control is disabled, keep disabled)
   * 4. Parent form's disabled state (if parent form is disabled, disable)
   *
   * This ensures dropdowns are automatically disabled throughout the application
   * for read-only users without needing to add [isDisabled] to each dropdown.
   */
  private updateDisabledState(): void {
    if (!this.control) {
      return;
    }

    // Priority 1: If isDisabled is explicitly set, use that value
    if (this.isDisabled !== undefined && this.isDisabled !== null) {
      if (this.isDisabled) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
      return;
    }

    // Priority 2: Check read-only user status
    const isReadOnly = this.readOnlyUserService.getIsReadOnlyUser();
    if (isReadOnly) {
      this.control.disable({ emitEvent: false });
      return;
    }

    // Priority 3 & 4: Respect control and parent form disabled state
    const isFormDisabled = this.isParentFormDisabled();
    const isControlDisabled = this.control.disabled;

    if (isFormDisabled || isControlDisabled) {
      // Keep disabled if form or control is disabled
      if (!isControlDisabled) {
        this.control.disable({ emitEvent: false });
      }
    } else {
      // Enable only if form is enabled and user is not read-only
      if (isControlDisabled) {
        this.control.enable({ emitEvent: false });
      }
    }
  }

  /**
   * Check if the parent form (FormGroup) is disabled
   */
  private isParentFormDisabled(): boolean {
    if (!this.control || !this.control.parent) {
      return false;
    }

    // Check if parent is a FormGroup and if it's disabled
    const parent = this.control.parent;
    if (parent instanceof FormGroup) {
      return parent.disabled;
    }

    // Traverse up the parent chain to find a disabled FormGroup
    let currentParent: any = parent;
    while (currentParent) {
      if (currentParent instanceof FormGroup && currentParent.disabled) {
        return true;
      }
      currentParent = currentParent.parent;
    }

    return false;
  }

  public onSelectionChange(change: MatSelectChange): void {
    this.selectionChange.emit(change);
  }

  toggleAllSelection() {
    if (this.allSelected) {
      this.select.options.forEach((item: MatOption) => item.select());
    } else {
      this.select.options.forEach((item: MatOption) => item.deselect());
    }
  }
}
